import { NewTrussGroup, TrussGroup } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { getSelectedItemEntries } from '../selectors/getSelectedItemSelector';
import { getPickingObjectInfo } from '../selectors/pickingSelectors';
import { getExistingTrussGroupNames } from '../selectors/trussGroups';
import {
  applicationOnInsertingTrussGroup,
  applicationOnUpdatePickingTrussEnvelopesForTrussGroup,
  clearInsertingObject,
  clearPickingObject,
} from '../slices/applicationSlice';
import { rightToolBarShowSet } from '../slices/panelDataSlice';
import { setHoveredJackComponentGuid } from '../slices/selectionsSlice';
import { trussGroupsLoaded } from '../slices/trussGroupsSlice';
import { selectItem } from './selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export function setHoveredJackComponentGuidIfApplicable(guid: string | null): ThunkActionVoid {
  return function setHoveredJackComponentGuidIfApplicableThunkAction(dispatch, getState) {
    if (getState().selections.hoveredJackComponentGuid !== guid) {
      dispatch(setHoveredJackComponentGuid(guid));
    }
  };
}

export function startInsertTrussGroup(): ThunkActionVoid {
  return function startInsertTrussGroupThunkAction(dispatch, getState) {
    const existingTrussGroupNames = getExistingTrussGroupNames(getState());
    const newTrussGroupName = getNewTrussGroupName(existingTrussGroupNames);
    dispatch(
      applicationOnInsertingTrussGroup({
        name: newTrussGroupName,
        trussEnvelopeGuids: getSelectedItemEntries(getState())
          .filter((entry) => entry.type === 'trussEnvelope')
          .map((entry) => entry.item.guid),
      }),
    );
  };
}

function getNewTrussGroupName(existingTrussGroupNames: string[]) {
  let newTrussGroupName = '1';
  while (existingTrussGroupNames.includes(newTrussGroupName)) {
    newTrussGroupName = (parseInt(newTrussGroupName, 10) + 1).toString();
  }
  return newTrussGroupName;
}

export function instantInsertTrussGroup(trussEnvelopeGuids: readonly string[]): AsyncThunkActionVoid {
  return async function instantInsertTrussGroupThunkAction(dispatch, getState) {
    const openContext = getState().application.openContext;
    if (openContext?.type !== 'project') throw new Error('Project not active');

    const existingTrussGroupNames = getExistingTrussGroupNames(getState());
    const newTrussGroupName = getNewTrussGroupName(existingTrussGroupNames);
    const newTrussGroup: NewTrussGroup = {
      name: newTrussGroupName,
      trussEnvelopeGuids,
      parentGuid: null,
    };

    await dispatch(finishInsertTrussGroup(newTrussGroup));
    dispatch(rightToolBarShowSet(true));
  };
}

export function finishInsertTrussGroup(newTrussGroup: NewTrussGroup): AsyncThunkActionVoid {
  return async function finishInsertTrussGroupThunkAction(dispatch, getState) {
    const openContext = getState().application.openContext;
    if (openContext?.type !== 'project') throw new Error('Project not active');

    const spinnerId = dispatch(addSpinnerMessage('Inserting Truss Group...'));

    try {
      dispatch(clearInsertingObject());
      dispatch(clearPickingObject());
      const createdTrussGroup = await designService.projects.createTrussGroup(openContext.projectGuid, newTrussGroup);
      dispatch(trussGroupsLoaded(createdTrussGroup));
      dispatch(selectItem(createdTrussGroup, 'trussGroup'));
    } catch (error) {
      console.error('Failed to finish inserting Truss Group:', error);
      dispatch(enqueueError('Failed to insert Truss Group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function updateTrussGroup(trussGroup: TrussGroup): AsyncThunkActionVoid {
  return async function updateTrussGroupThunkAction(dispatch, getState) {
    const openContext = getState().application.openContext;
    if (openContext?.type !== 'project') throw new Error('Project not active');

    const spinnerId = dispatch(addSpinnerMessage('Updating Truss Group...'));

    const oldTrussGroup = getState().trussGroups[trussGroup.guid];

    try {
      // Optimistically update the Truss Group in the store to avoid a brief period where the highlighted Truss
      // Envelopes are those of the old Truss Group
      dispatch(trussGroupsLoaded(trussGroup));

      await designService.projects.updateTrussGroup(openContext.projectGuid, trussGroup);
    } catch (error) {
      console.error('Failed to finish updating Truss Group:', error);
      dispatch(enqueueError('Failed to update Truss Group'));
      dispatch(trussGroupsLoaded(oldTrussGroup));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function updatePickingObjectInfoForChosenTrussEnvelopes(trussEnvelopeGuids: readonly string[]): ThunkActionVoid {
  return function updatePickingObjectInfoForChosenTrussEnvelopesThunkAction(dispatch, getState) {
    const pickingObjectInfo = getPickingObjectInfo(getState());
    if (pickingObjectInfo?.type !== 'pickingTrussEnvelopesForTrussGroup')
      throw new Error('Not picking truss envelopes for truss group');

    const insertingObjectInfo = getState().application.insertingObjectInfo;
    let currentTrussEnvelopeGuids: readonly string[];
    if (pickingObjectInfo.trussGroupGuid !== null) {
      currentTrussEnvelopeGuids = getState().trussGroups[pickingObjectInfo.trussGroupGuid].trussEnvelopeGuids;
    } else {
      if (insertingObjectInfo?.type !== 'trussGroup')
        throw new Error('Expected insertingObjectInfo to be a TrussGroup');
      currentTrussEnvelopeGuids = insertingObjectInfo.data.trussEnvelopeGuids;
    }

    const trussEnvelopeGuidsToAdd: Set<string> = new Set(pickingObjectInfo.trussEnvelopeGuidsToAdd);
    const trussEnvelopeGuidsToRemove: Set<string> = new Set(pickingObjectInfo.trussEnvelopeGuidsToRemove);
    for (const trussEnvelopeGuid of trussEnvelopeGuids) {
      if (pickingObjectInfo.trussEnvelopeGuidsToAdd.includes(trussEnvelopeGuid)) {
        trussEnvelopeGuidsToAdd.delete(trussEnvelopeGuid);
      } else if (pickingObjectInfo.trussEnvelopeGuidsToRemove.includes(trussEnvelopeGuid)) {
        trussEnvelopeGuidsToRemove.delete(trussEnvelopeGuid);
      } else if (currentTrussEnvelopeGuids.includes(trussEnvelopeGuid)) {
        trussEnvelopeGuidsToRemove.add(trussEnvelopeGuid);
      } else {
        trussEnvelopeGuidsToAdd.add(trussEnvelopeGuid);
      }
    }

    dispatch(
      applicationOnUpdatePickingTrussEnvelopesForTrussGroup({
        trussEnvelopeGuidsToAdd: Array.from(trussEnvelopeGuidsToAdd),
        trussEnvelopeGuidsToRemove: Array.from(trussEnvelopeGuidsToRemove),
      }),
    );
  };
}
